/**
 * Radius System
 * Border radius values built on our spacing foundation for consistency
 */

import { SpacingToken, RawSpacingToken, resolveSpacing, rawSpacing } from "./Spacing";

// Branded type for radius
declare const __radius_brand: unique symbol;

export interface RadiusToken {
  readonly [__radius_brand]: true;
  readonly _spacingToken: SpacingToken | RawSpacingToken;
  readonly _value: string;
}

// Factory function
const createRadiusToken = (spacingToken: SpacingToken | RawSpacingToken): RadiusToken => ({ _spacingToken: spacingToken, _value: resolveSpacing(spacingToken) } as RadiusToken);

// Resolver function for radius tokens
export const resolveRadius = (token: RadiusToken): string => resolveSpacing(token._spacingToken);

// Public API - semantic radius values based on spacing
export const Radius = {
  // None (sharp corners)
  none: createRadiusToken(rawSpacing[0]), // 0px

  // Size variants (based on spacing scale)
  tiny: createRadiusToken(rawSpacing[1]), // 4px
  small: createRadiusToken(rawSpacing[2]), // 8px
  medium: createRadiusToken(rawSpacing[3]), // 12px
  large: createRadiusToken(rawSpacing[4]), // 16px

  // Larger radius values
  extraLarge: createRadiusToken(rawSpacing[5]), // 20px
  huge: createRadiusToken(rawSpacing[6]), // 24px

  // Special values
  full: createRadiusToken(rawSpacing[12]), // Very large value for pill shapes (48px)

  // Semantic aliases for components
  button: createRadiusToken(rawSpacing[2]), // 8px - standard button radius
  card: createRadiusToken(rawSpacing[3]), // 12px - card radius
  input: createRadiusToken(rawSpacing[2]), // 8px - input field radius
  modal: createRadiusToken(rawSpacing[4]), // 16px - modal radius
  tag: createRadiusToken(rawSpacing[1]), // 4px - tag radius
  avatar: createRadiusToken(rawSpacing[2]), // 8px - avatar radius
  image: createRadiusToken(rawSpacing[3]), // 12px - image radius
} as const;
